import { FC, useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { toast } from 'react-toastify';
import { formatTime } from '../../utils/commonFn';
import { RECSYS_GRADE, SAVE_RECSYS_GRADE } from './GradeWidget.gql';

interface Props {
  jobOrderId: string;
  candidateId: string;
}

const LABELS: Array<{ v: 0 | 1 | 2 | 3; label: string }> = [
  { v: 0, label: 'Not a fit' },
  { v: 1, label: 'Weak' },
  { v: 2, label: 'Good' },
  { v: 3, label: 'Excellent' },
];

// D17 "Rate this match" — compact 0-3 segmented grader + optional one-line
// note. ONE SHARED grade per (jobOrderId, candidateId), last edit wins.
// Optimistic: the pick/note is reflected instantly, persisted via
// saveRecsysGrade, and reverted with a toast on failure.
const GradeWidget: FC<Props> = ({ jobOrderId, candidateId }) => {
  const [grade, setGrade] = useState<number | null>(null);
  const [note, setNote] = useState('');
  const [savedNote, setSavedNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);

  const { data } = useQuery(RECSYS_GRADE, {
    variables: { jobOrderId, candidateId },
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    const existing = data?.recsysGrade;
    setGrade(existing?.grade ?? null);
    setNote(existing?.note ?? '');
    setSavedNote(existing?.note ?? '');
    setLastSavedAt(existing?.updatedAt ? new Date(existing.updatedAt) : null);
    // Widget is remounted per candidate via `key`, so this only needs to seed
    // state once the read query resolves for the current candidate.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const [saveGrade] = useMutation(SAVE_RECSYS_GRADE);

  const handlePick = (v: number) => {
    const prevGrade = grade;
    setGrade(v);
    setSaving(true);
    saveGrade({ variables: { jobOrderId, candidateId, grade: v, note: note.trim() || null } })
      .then(() => {
        setLastSavedAt(new Date());
        setSavedNote(note);
      })
      .catch(() => {
        setGrade(prevGrade);
        toast.error('Could not save grade. Please try again.');
      })
      .finally(() => setSaving(false));
  };

  const handleNoteSave = () => {
    if (grade === null) return;
    const trimmed = note.trim();
    if (trimmed === savedNote) return;
    setSaving(true);
    saveGrade({ variables: { jobOrderId, candidateId, grade, note: trimmed || null } })
      .then(() => {
        setLastSavedAt(new Date());
        setSavedNote(trimmed);
      })
      .catch(() => {
        setNote(savedNote);
        toast.error('Could not save note. Please try again.');
      })
      .finally(() => setSaving(false));
  };

  const savedStateText = saving
    ? 'Saving…'
    : lastSavedAt
    ? `Last saved ${formatTime(lastSavedAt)}`
    : null;

  return (
    <div className="grade-widget">
      <div className="grade-widget__row">
        <span className="grade-widget__title">Rate this match</span>
        {savedStateText && <span className="grade-widget__saved">{savedStateText}</span>}
      </div>

      <div className="grade-widget__segments" role="group" aria-label="Match grade">
        {LABELS.map(({ v, label }) => (
          <button
            key={v}
            type="button"
            className={`grade-widget__segment grade-widget__segment--${v}${grade === v ? ' active' : ''}`}
            aria-label={label}
            title={label}
            aria-pressed={grade === v}
            onClick={() => handlePick(v)}
          >
            {v}
          </button>
        ))}
      </div>

      <input
        type="text"
        className="grade-widget__note"
        maxLength={280}
        placeholder="Optional note (one line)"
        value={note}
        disabled={grade === null}
        onChange={(e) => setNote(e.target.value)}
        onBlur={handleNoteSave}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.currentTarget.blur();
          }
        }}
      />
    </div>
  );
};

export default GradeWidget;
